/**
 * Log Limiter
 * Rate limiting, deduplication, and throttling for logs
 */
import { datadogLogs } from '@datadog/browser-logs';
import store from 'store2';
import { log } from './consoleLogger';

const defaultConfig = {
  WINDOW_DURATION_MS: 60_000,
  DEDUP_LIMIT: 100,
  DEDUP_EXPIRY_MS: 5 * 60_000,
  globalLimiter: {
    maxPerMinute: 60,
  },
  levelLimits: {
    debug: 3,
    info: 5,
    warn: 10,
    error: 20,
  },
};

let config = { ...defaultConfig };

const dedupCache = new Map();
const rateLimitCache = new Map();

const globalLimiter = {
  calls: [],
  maxPerMinute: config.globalLimiter.maxPerMinute,
};

const SESSION_KEY = 'log_session_id';

const getSessionId = () => {
  let id = store.session.get(SESSION_KEY);
  if (!id) {
    id = Date.now().toString(36) + Math.random().toString(36).slice(2);
    store.session.set(SESSION_KEY, id);
  }
  return String(id);
};

export function configureLogLimiter(newConfig) {
  config = {
    ...config,
    ...newConfig,
    globalLimiter: {
      ...config.globalLimiter,
      ...newConfig.globalLimiter,
    },
    levelLimits: {
      ...config.levelLimits,
      ...newConfig.levelLimits,
    },
  };

  globalLimiter.maxPerMinute = config.globalLimiter.maxPerMinute;
  log('Log limiter configuration updated:', config);
}

function getKey(entry) {
  const stackFirstLine =
    typeof entry.data?.stack === 'string' ? entry.data.stack.split('\n')[0] : '';
  return entry.key || `${entry.message}::${stackFirstLine}`;
}

export function logWithLimiter(entry) {
  const key = getKey(entry);
  const now = Date.now();
  const level = entry.level || 'error';

  globalLimiter.calls = globalLimiter.calls.filter((t) => now - t < config.WINDOW_DURATION_MS);
  if (globalLimiter.calls.length >= globalLimiter.maxPerMinute) {
    if (Math.random() < 0.01) {
      log('Global log rate limit exceeded, dropping log', {});
      datadogLogs.logger.warn('Log rate exceeded global limit', {
        dropped: true,
        sampledMessage: entry.message.substring(0, 100),
        samplingRate: 0.01,
      });
    }
    return;
  }
  globalLimiter.calls.push(now);

  const state =
    rateLimitCache.get(key) || {
      count: 0,
      windowStart: now,
      recentCalls: [],
    };

  state.recentCalls = state.recentCalls.filter((t) => now - t < config.WINDOW_DURATION_MS);
  state.count = state.recentCalls.length;

  const levelLimit = config.levelLimits[level];

  if (state.count >= levelLimit) {
    if (Math.random() < 0.1) {
      const sampledPayload = {
        ...entry.data,
        sampled: true,
        sampleRate: 0.1,
        occurrenceCount: state.count,
        tags: entry.tags || [],
        timestamp: new Date().toISOString(),
        pageURL: typeof window !== 'undefined' ? window.location.href : '',
      };
      log('Log rate limit exceeded for level, dropping log', sampledPayload);
      datadogLogs.logger[level](`[SAMPLED] ${entry.message}`, sampledPayload);
    }
    return;
  }

  state.recentCalls.push(now);
  state.count++;
  rateLimitCache.set(key, state);

  const dedupEntry = dedupCache.get(key);
  if (dedupEntry && now - dedupEntry.timestamp < config.DEDUP_EXPIRY_MS) {
    return;
  }

  dedupCache.set(key, { timestamp: now });

  if (Math.random() < 0.1) {
    cleanupDedupCache(now);
  }

  const payload = {
    ...entry.data,
    tags: entry.tags,
    timestamp: new Date().toISOString(),
    pageURL: typeof window !== 'undefined' ? window.location.href : '',
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
    viewport:
      typeof window !== 'undefined'
        ? { width: window.innerWidth, height: window.innerHeight }
        : { width: 0, height: 0 },
    sessionId: getSessionId(),
  };

  log('Logging entry with limiter', { key, level, payload });
  datadogLogs.logger[level](entry.message, payload);
}

function cleanupDedupCache(now) {
  for (const [key, entry] of dedupCache.entries()) {
    if (now - entry.timestamp > config.DEDUP_EXPIRY_MS) {
      dedupCache.delete(key);
    }
  }

  if (dedupCache.size > config.DEDUP_LIMIT) {
    const entriesToRemove = Math.ceil(config.DEDUP_LIMIT * 0.25);

    if (dedupCache.size > 0) {
      const timestamps = Array.from(dedupCache.values()).map((e) => e.timestamp);
      const oldestTime = Math.min(...timestamps);

      let removed = 0;
      for (const [k, e] of dedupCache.entries()) {
        if (removed >= entriesToRemove) break;
        if (e.timestamp <= oldestTime + 10000) {
          dedupCache.delete(k);
          removed++;
        }
      }

      if (removed < entriesToRemove && dedupCache.size > config.DEDUP_LIMIT - entriesToRemove) {
        const extraToRemove = entriesToRemove - removed;
        let count = 0;
        for (const k of dedupCache.keys()) {
          if (count >= extraToRemove) break;
          dedupCache.delete(k);
          count++;
        }
      }
    }
  }
}

export function getLogLimiterConfig() {
  return config;
}

export function resetLoggingState() {
  dedupCache.clear();
  rateLimitCache.clear();
  globalLimiter.calls = [];
}

export const defaultLogLimiterConfig = { ...defaultConfig };

// Default export for backward compatibility
export default function createLogLimiter(options = {}) {
  // Backward-compatible simple limiter used by callers that only need an allow/deny function
  const maxCalls = typeof options.limit === 'number' ? options.limit : config.globalLimiter.maxPerMinute;
  const intervalMs = typeof options.intervalMs === 'number' ? options.intervalMs : config.WINDOW_DURATION_MS;
  let calls = [];

  const shouldLog = () => {
    const now = Date.now();
    calls = calls.filter((timestamp) => now - timestamp < intervalMs);
    if (calls.length >= maxCalls) {
      return false;
    }
    calls.push(now);
    return true;
  };

  return Object.assign(shouldLog, {
    logWithLimiter,
    configureLogLimiter,
    getLogLimiterConfig,
    resetLoggingState,
    defaultLogLimiterConfig,
  });
}
